import type {
  CompanyLookupResponse,
  CompanySearchHit,
  LastAccountsResponse,
} from '@dormant/shared';

async function request<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(path, { signal });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || `Request failed (${res.status})`);
  }
  return data as T;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`);
  return data as T;
}

export interface CreateOrderPayload {
  email: string;
  companyNumber: string;
  companyName: string;
  directorName: string;
  isFirstFiling: boolean;
  periodStart: string;
  periodEnd: string;
  authCode: string;
  figures: {
    fixedAssets: number;
    currentAssets: number;
    creditorsDueWithinOneYear: number;
    creditorsDueAfterOneYear: number;
    calledUpShareCapital: number;
    profitAndLossAccount: number;
    netAssets: number;
  };
}

export interface OrderStatus {
  id: string;
  status: 'PENDING_PAYMENT' | 'PAID' | 'SUBMITTING' | 'ACCEPTED' | 'REJECTED';
  companyName: string;
  companyNumber: string;
  periodEnd: string;
  amountPence: number;
  chAckNumber: string | null;
  errorReason: string | null;
}

export const api = {
  searchCompanies: (q: string, signal?: AbortSignal) =>
    request<{ items: CompanySearchHit[] }>(
      `/api/company/search?q=${encodeURIComponent(q)}`,
      signal,
    ),
  lookupCompany: (num: string) =>
    request<CompanyLookupResponse>(`/api/company/${encodeURIComponent(num)}`),
  lastAccounts: (num: string) =>
    request<LastAccountsResponse>(
      `/api/company/${encodeURIComponent(num)}/last-accounts`,
    ),
  createOrder: (payload: CreateOrderPayload) =>
    postJson<{ orderId: string; checkoutUrl: string }>('/api/orders', payload),
  getOrder: (id: string) =>
    request<OrderStatus>(`/api/orders/${encodeURIComponent(id)}`),
};
